/* eslint-disable */
/**
 * Navigation helpers for the National Partners Local Sourcing flow.
 *
 * - deriveCartUrl: Appends `/cart` to a retailer's base origin + pathname
 * - buildAgreementNavigationState: Validates context and produces router state for agreement screen
 * - buildAgreementPath: Produces the agreement navigation path with query params
 */

export interface AgreementItem {
  itemNumber: string;
  weeklyRate: string;
  weeklyTerm: string;
  cashPrice: string;
  model: string;
  brand?: string;
  amount: string;
  modelNumber: string;
}

export interface AgreementRouterState {
  spendLimitId: string;
  storeNumber: string;
  vendorName: string;
  vendorId: string | null;
  supplierName: string;
  supplierOrderNumber: string;
  items: AgreementItem[];
}

export interface NavigationStateContext {
  spendLimitId: string;
  storeNumber: string;
  vendorName: string;
  vendorId: string | null;
  supplierName: string;
  supplierOrderNumber: string;
  resolvedProducts: Array<{
    itemNumber: string;
    weeklyRate: string;
    weeklyTerm: string;
    cashPrice: string;
    model: string;
    brand?: string;
    costPerItem: string;
    modelNumber: string;
    quantity?: string;
  }>;
}

/**
 * Derives a retailer cart URL by appending `/cart` to the base origin + pathname
 * (with any trailing slash removed).
 *
 * Example: "https://www.bestbuy.com" → "https://www.bestbuy.com/cart"
 * Example: "https://www.homedepot.com/shop/" → "https://www.homedepot.com/shop/cart"
 *
 * Requirements: 3.7
 */
export function deriveCartUrl(externalLaunchUrl: string): string {
  try {
    const url = new URL(externalLaunchUrl);
    const pathname = url.pathname.endsWith('/')
      ? url.pathname.slice(0, -1)
      : url.pathname;
    return `${url.origin}${pathname}/cart`;
  } catch {
    // If URL parsing fails, fall back to simple string manipulation
    const trimmed = externalLaunchUrl.endsWith('/')
      ? externalLaunchUrl.slice(0, -1)
      : externalLaunchUrl;
    return `${trimmed}/cart`;
  }
}

/**
 * Validates all required fields and builds the AgreementRouterState for navigation.
 * Returns the state object if valid, or an error object with details of what's missing.
 *
 * Requirements: 5.2, 5.3, 5.5, 5.7
 */
export function buildAgreementNavigationState(
  context: NavigationStateContext
): AgreementRouterState | { error: string } {
  const missingFields: string[] = [];

  if (!context.spendLimitId) missingFields.push('spendLimitId');
  if (!context.storeNumber) missingFields.push('storeNumber');
  if (!context.vendorName) missingFields.push('vendorName');
  if (!context.supplierName) missingFields.push('supplierName');
  if (!context.supplierOrderNumber) missingFields.push('supplierOrderNumber');

  if (!context.resolvedProducts || context.resolvedProducts.length === 0) {
    missingFields.push('items (no items provided)');
  } else if (context.resolvedProducts.length > 4) {
    missingFields.push(`items (${context.resolvedProducts.length} items exceeds maximum of 4)`);
  }

  if (missingFields.length > 0) {
    return { error: `Missing required fields: ${missingFields.join(', ')}` };
  }

  // Validate each item has all required fields
  const itemErrors: string[] = [];
  const items: AgreementItem[] = context.resolvedProducts.map((product, index) => {
    const missing: string[] = [];
    if (!product.itemNumber) missing.push('itemNumber');
    if (!product.weeklyRate) missing.push('weeklyRate');
    if (!product.weeklyTerm) missing.push('weeklyTerm');
    if (!product.cashPrice) missing.push('cashPrice');
    if (!product.costPerItem) missing.push('amount');
    if (!product.model) missing.push('model');

    if (missing.length > 0) {
      itemErrors.push(`Item ${index + 1} missing: ${missing.join(', ')}`);
    }

    return {
      itemNumber: product.itemNumber,
      weeklyRate: product.weeklyRate,
      weeklyTerm: product.weeklyTerm,
      cashPrice: product.cashPrice,
      model: product.model,
      brand: (product as any).brand || '',
      amount: product.costPerItem,
      modelNumber: product.modelNumber,
    };
  });

  if (itemErrors.length > 0) {
    return { error: `Incomplete item data: ${itemErrors.join('; ')}` };
  }

  return {
    spendLimitId: context.spendLimitId,
    storeNumber: context.storeNumber,
    vendorName: context.vendorName,
    vendorId: context.vendorId,
    supplierName: context.supplierName,
    supplierOrderNumber: context.supplierOrderNumber,
    items,
  };
}

/**
 * Builds the agreement navigation path with customerId and spendLimitId query parameter.
 *
 * Produces: `/agreement/customerorder/details/{customerId}?spendLimitId={spendLimitId}`
 *
 * Requirements: 5.1
 */
export function buildAgreementPath(customerId: string, spendLimitId: string): string {
  return `/agreement/customerorder/details/${customerId}?spendLimitId=${spendLimitId}`;
}
